// Thin HTTP client for the dedicated billing service. Mirrors apiClient.ts
// (token refresh + Authorization header + JSON parsing) but targets the
// billing API base URL and tags every request with X-Eleplan-Platform so the
// server-side platform-resolver can choose the right plan list (Apple on iOS,
// Stripe on Android/web).

import { Platform } from 'react-native';
import config from '../../config';
import { getFreshToken } from '../../utilities/apiClient';

const platformHeader = (): Record<string, string> => {
  const p = Platform.OS === 'ios' ? 'ios' : Platform.OS === 'android' ? 'android' : 'web';
  return { 'X-Eleplan-Platform': p };
};

async function parseResponse(res: Response): Promise<any> {
  const text = await res.text();
  if (!text.trim()) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { success: false, message: res.statusText || 'Invalid response' };
  }
}

function failure(res: Response, data: any) {
  return {
    success: false,
    status: res.status,
    error: data?.error,
    errorCode: data?.errorCode,
    message: data?.message || res.statusText || `HTTP ${res.status}`,
  };
}

export const billingGet = async (path: string): Promise<any> => {
  const token = await getFreshToken();
  const res = await fetch(`${config.billingApi.endpoint}${path}`, {
    headers: { Authorization: token, ...platformHeader() },
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

export const billingPost = async (path: string, body: any): Promise<any> => {
  const token = await getFreshToken();
  const res = await fetch(`${config.billingApi.endpoint}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
      ...platformHeader(),
    },
    body: JSON.stringify(body ?? {}),
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};

export const billingDelete = async (path: string): Promise<any> => {
  const token = await getFreshToken();
  const res = await fetch(`${config.billingApi.endpoint}${path}`, {
    method: 'DELETE',
    headers: { Authorization: token, ...platformHeader() },
  });
  const data = await parseResponse(res);
  if (!res.ok) return failure(res, data);
  return data;
};
